import React from 'react';
import Column from '@amzn/meridian/column';
import LinearGauge from '@amzn/meridian/linear-gauge';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { EMPTY_TABLE_VALUE } from 'src/constants';
import { type SyncTargetDto } from 'src/types/sync-target.types';
import {
  type BoxFolderProgressKind,
  deriveBoxFolderProgress,
  relativeFromNow,
} from 'src/utils/boxFolderProgress';

const GAUGE_TRACK_WIDTH = 4;

/**
 * Typed `Partial` on purpose, matching `BoxFolderStatusTag`: a kind added to the derivation
 * later arrives here without an entry, and the lookup miss falls through to the table's
 * "no data" value rather than rendering `t(undefined)`.
 *
 * `UNREPORTED` deliberately has **no** entry. It means the row completed a scan before Bender
 * shipped these counters, so there is genuinely nothing to name — `--` is the honest answer,
 * and naming it would imply a state the folder is in rather than data we lack.
 */
const PROGRESS_LABEL_KEYS: Partial<Record<BoxFolderProgressKind, string>> = {
  NEVER_SCANNED: 'box_folder_progress_not_scanned',
  SCANNING: 'box_folder_progress_scanning',
  NO_ASSETS: 'box_folder_progress_no_assets',
  SYNCING: 'box_folder_progress_syncing',
  SYNCED: 'box_folder_progress_synced',
};

interface BoxFolderSyncProgressProps {
  syncTarget: SyncTargetDto;
  testId?: string;
}

/**
 * How far qualification has got through one enrolled Box folder.
 *
 * This is deliberately a separate column from `BoxFolderStatusTag`, not a line inside it.
 * Bender puts no status guard on the progress counters and qualification keeps running after
 * the row's `status` has already returned to `ACTIVE`, so the normal state of a syncing folder
 * is "Active" *and* "Syncing assets" at once. Folding the two together would force one to
 * overwrite the other. Neither may gate the other.
 *
 * Renders cell contents only — the row owns the `DataTableCell`.
 */
export const BoxFolderSyncProgress: React.FC<BoxFolderSyncProgressProps> = ({
  syncTarget,
  testId,
}) => {
  const { t } = useTranslation();

  const progress = deriveBoxFolderProgress(syncTarget);
  const labelKey = PROGRESS_LABEL_KEYS[progress.kind];
  const fileCount = 'fileCount' in progress ? progress.fileCount : undefined;
  const relative = progress.kind === 'SYNCING'
    ? relativeFromNow(progress.lastQualifiedAt)
    : undefined;

  /*
   * Drawn only for the two states that have a real denominator. `NO_ASSETS` has one of zero,
   * which Meridian clips to an empty track rather than crashing — but an empty bar reads as
   * "nothing done" when the truth is "nothing to do". `SYNCED` is expressed as 1-of-1 because
   * the derived state drops the totals once complete, and a full bar is a full bar.
   *
   * A single-batch folder does get a bar, pinned at 0% until that batch lands: chunky, since
   * batches hold up to 50 files, but honest, and it keeps row height uniform down the column.
   *
   * `label` is derived per state rather than shared, because the gauge is a `role="img"` whose
   * accessible name is announced on its own: reusing the syncing phrasing announced "Syncing
   * assets, 100% complete" on a folder whose visible label read "Sync complete".
   */
  const gauge = progress.kind === 'SYNCING'
    ? {
        value: progress.qualified,
        maxValue: progress.total,
        percent: progress.percent,
        label: t('box_folder_progress_gauge_label', { percent: progress.percent }),
      }
    : progress.kind === 'SYNCED'
      ? { value: 1, maxValue: 1, percent: 100, label: t('box_folder_progress_synced') }
      : undefined;

  return (
    <Column data-testid={testId} spacing="100">
      {/*
        The percentage is shown beside the label, not only inside the gauge's accessible name:
        a bar alone cannot be read precisely at this width, and with batches of up to 50 files
        the difference between 60% and 80% is thousands of files. Both come from the same
        derived `percent`, so the number and the bar cannot disagree.
      */}
      <Row alignmentVertical="center" spacing="200">
        <Text data-testid={testId ? `${testId}-label` : undefined}>
          {labelKey ? t(labelKey) : EMPTY_TABLE_VALUE}
        </Text>
        {gauge && (
          <Text color="secondary" type="b200">
            {t('box_folder_progress_percent', { percent: gauge.percent })}
          </Text>
        )}
      </Row>

      {gauge && (
        <LinearGauge
          aria-label={gauge.label}
          data-testid={testId ? `${testId}-gauge` : undefined}
          maxValue={gauge.maxValue}
          minValue={0}
          trackWidth={GAUGE_TRACK_WIDTH}
          value={gauge.value}
          width="100%"
        />
      )}

      {/*
        "files", never "assets": this counts what discovery found before qualification, so it
        includes files later excluded, unchanged, or unprobeable.
      */}
      {fileCount !== undefined && (
        <Text color="secondary" type="b200">
          {t('box_folder_progress_files', { count: fileCount })}
        </Text>
      )}

      {/*
        Stated, not judged. Bender's redrive tail runs 30-90 minutes, so any threshold short
        enough to be useful would flag healthy syncs as stalled — the user gets the fact and
        draws their own conclusion.
      */}
      {relative && (
        <Text color="secondary" type="b200">
          {t('box_folder_progress_updated', { when: relative })}
        </Text>
      )}
    </Column>
  );
};
